import Set from '@ungap/set';
import { PaymentMethodType } from '../enums/Tokens';
import { CreditCard } from './credit-card/CreditCard';
import PaymentMethodContext from '../core/PaymentMethodContext';
import normalizeOptions from './normalizeOptions';
import window from '../utils/window';
import { createStyleManager, IStyleManager } from '../checkout/ui/StyleManager';
import { ClientContext } from '../core/ClientContextFactory';
import { BasePaymentMethod } from './BasePaymentMethod';
import { getPaymentMethodModule } from '../utils/ModuleResolver';
import CheckoutStore from '../store/CheckoutStore';

type PaymentMethodDefinition = {
  type: PaymentMethodType;
  label: string;
  createPaymentMethod?: (
    paymentMethods,
    inputOptions,
    remoteConfig?,
    styleManager?,
  ) => BasePaymentMethod;
  key: string;
  canVault?: boolean;
};

interface InstantiablePaymentMethod {
  new (paymentMethods, inputOptions, remoteConfig?, styleManager?);
}

export const definitions: Array<PaymentMethodDefinition> = [
  {
    type: PaymentMethodType.PAYMENT_CARD,
    label: 'Card',
    createPaymentMethod: (context, options) => new CreditCard(context, options),
    key: 'card',
  },
  {
    type: PaymentMethodType.APPLE_PAY,
    label: 'Apple Pay',
    key: 'applePay',
  },
  {
    type: PaymentMethodType.GOOGLE_PAY,
    label: 'Google Pay',
    key: 'googlePay',
  },
  {
    type: PaymentMethodType.PAYPAL,
    label: 'PayPal',
    key: 'paypal',
  },
  {
    type: PaymentMethodType.GO_CARDLESS,
    label: 'Go Cardless',
    key: 'directDebit',
  },
  {
    type: PaymentMethodType.KLARNA,
    label: 'Klarna',
    key: 'klarna',
  },
];

export const getPaymentMethodDefinitions = (
  availableDefinitions: Array<PaymentMethodDefinition>,
  allowedPaymentMethods:
    | PaymentMethodType
    | Array<PaymentMethodType>
    | undefined,
) => {
  // Undefined
  if (!allowedPaymentMethods) {
    allowedPaymentMethods = availableDefinitions.map(
      (defintion) => defintion.type,
    );
  } else if (!Array.isArray(allowedPaymentMethods)) {
    // Just a payment method
    allowedPaymentMethods = [allowedPaymentMethods];
  }

  const allowedPaymentMethodSet = new Set(allowedPaymentMethods);

  return availableDefinitions.filter((definition) =>
    allowedPaymentMethodSet.has(definition.type),
  );
};

export const PaymentMethods = {
  async create(
    context: ClientContext,
    opts: Record<string, unknown>,
    store: CheckoutStore,
    styleManager: IStyleManager = createStyleManager(),
  ): Promise<Record<string, BasePaymentMethod>> {
    const options = normalizeOptions(opts);

    const paymentMethods = PaymentMethodContext.create(context, options, store);
    const paymentMethodDefinitions = getPaymentMethodDefinitions(
      definitions,
      options.paymentMethod ?? options.allowedPaymentMethods,
    );

    const ops = paymentMethodDefinitions.map(async (definition) => {
      const { type, key, label } = definition;

      const inputOptions = options[key] || {};

      const remoteConfig = context.session.paymentMethods.find(
        (elm) => elm.type === type,
      );

      if (!remoteConfig) {
        if (options.logWarnings) {
          window.console.warn(
            `Options for "${label}" were provided but no matching configuration was found for your Primer account.`,
          );
        }
        return null;
      }

      let { createPaymentMethod } = definition;
      if (!createPaymentMethod) {
        const PaymentMethodModule = getPaymentMethodModule(context, type);
        try {
          const PaymentMethod = ((await PaymentMethodModule.import()) as unknown) as InstantiablePaymentMethod;

          // Create factory method
          createPaymentMethod = (
            _paymentMethods,
            _inputOptions,
            _remoteConfig?,
            _styleManager?,
          ) =>
            new PaymentMethod(
              _paymentMethods,
              _inputOptions,
              _remoteConfig,
              _styleManager,
            );
        } catch (e) {
          console.warn(`Can't load ${type}`);
          return null;
        }
      }

      const paymentMethod = createPaymentMethod(
        paymentMethods,
        inputOptions,
        remoteConfig,
        styleManager,
      ) as BasePaymentMethod;

      if (options.vaultOnly && !paymentMethod.canVault) {
        // TODO: return this info server-side. This will do for now
        return null;
      }

      let loaded: boolean;
      try {
        loaded = await paymentMethod.setupAndValidate();
      } catch (e) {
        console.warn(`Can't init ${type}`, e);
        loaded = false;
      }

      if (!loaded) {
        return null;
      }

      if (options.mountImmediately) {
        await paymentMethod.mount();
      }

      return { [key]: paymentMethod };
    });

    const results = await Promise.all(ops);

    return Object.assign({}, ...results);
  },
};
